import { useNavigate } from "@tanstack/react-router";
import {
  AlertOctagonIcon,
  CheckCircle2Icon,
  CheckSquare2Icon,
  ChevronsUpIcon,
  ContactIcon,
  CopyPlusIcon,
  DollarSignIcon,
  FileCheckIcon,
  FileIcon,
  FileUpIcon,
  FlaskConicalIcon,
  MoreHorizontalIcon,
  PackageIcon,
  PencilIcon,
  TagIcon,
  TestTubeIcon,
  TrashIcon,
  UploadIcon,
  User2Icon,
  XCircleIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import type { Key } from "react-aria";

import { RoleCondition } from "@/components/RoleCondition";
import { Button } from "@/components/ui/button";
import { StatusIcon } from "@/components/ui/icons";
import {
  Menu,
  MenuItem,
  MenuKeyboard,
  MenuSeparator,
  MenuSubTrigger,
  MenuTrigger,
} from "@/components/ui/menu";
import { useAccessionCommands } from "./AccessionCommands";
import { useAccession } from "./AccessionProvider";
import { statusCommands } from "./commands/SetStatusCommands";
import { type DialogName, useSetDialogState } from "./detail/state";

export function ActionDropdown() {
  const { accession, testOrders } = useAccession();
  const commands = useAccessionCommands();
  const navigate = useNavigate();

  const setDialog = useSetDialogState();

  const setDialogState: typeof setDialog = useCallback(
    (dialog: DialogName, flag) => {
      setDialog(dialog, flag);
    },
    [setDialog],
  );

  const referenceLabs = useReferenceLabs();

  const onAction = useCallback(
    (action: Key) => {
      switch (action) {
        case "receive":
          setDialogState("receive", true);
          break;
        case "unreceive":
          setDialogState("unreceive", true);
          break;
        case "hold":
          setDialogState("hold", true);
          break;
        case "enterResults": {
          const order = testOrders.find((to) => !to.isReflex);
          setDialogState("enterResults", order ? { orderId: order.id } : null);
          break;
        }
        case "generateReport": {
          commands.generateReport();
          break;
        }
        case "upload": {
          setDialogState("upload", { files: [] });
          break;
        }
        // case "clone": {
        //   setDialogState("clone", true);
        //   break;
        // }
        case "instrument": {
          setDialogState("instrument", true);
          break;
        }
        case "setPriorityStat": {
          commands.setPriority("STAT");
          break;
        }
        case "setPriorityRoutine": {
          commands.setPriority("ROUTINE");
          break;
        }
        case "printLabels": {
          commands.printLabels();
          break;
        }
        case "printArchiveLabels": {
          commands.printArchiveLabels();
          break;
        }
        case "editDonor": {
          setDialogState("editDonor", true);
          break;
        }
        case "moveToSubject": {
          setDialogState("moveToSubject", true);
          break;
        }
        case "editClient": {
          setDialogState("editClient", true);
          break;
        }
        case "addTests": {
          setDialogState("addTests", true);
          break;
        }
        case "addSample": {
          setDialogState("addSample", true);
          break;
        }
        case "editAdditional": {
          setDialogState("editAdditional", true);
          break;
        }
        case "editBilling": {
          setDialogState("editBilling", true);
          break;
        }
        case "restore": {
          setDialogState("restore", true);
          break;
        }
        case "cancel": {
          setDialogState("cancel", true);
          break;
        }
        case "delete": {
          setDialogState("delete", true);
          break;
        }
      }
    },
    [setDialogState, testOrders, commands],
  );

  return (
    <MenuTrigger>
      <Button size="icon" variant="ghost">
        <MoreHorizontalIcon />
      </Button>
      <Menu aria-label="Accession actions" onAction={onAction}>
        {accession.status === "new" ? (
          <MenuItem id="receive">
            <StatusIcon status="received" noColor />
            Mark received
            <MenuKeyboard>R</MenuKeyboard>
          </MenuItem>
        ) : accession.status === "received" ? (
          <MenuItem id="unreceive">
            <StatusIcon status="new" noColor />
            Mark not received
            <MenuKeyboard>R</MenuKeyboard>
          </MenuItem>
        ) : null}
        {accession.status !== "cancelled" && (
          <>
            <MenuItem id="hold">
              <AlertOctagonIcon />
              Hold
              <MenuKeyboard>H</MenuKeyboard>
            </MenuItem>
            <RoleCondition roles={["blis_lab_manager", "blis_lab_tech"]}>
              <MenuItem id="enterResults" isDisabled={testOrders.length === 0}>
                <CheckSquare2Icon />
                Enter results
                <MenuKeyboard>E</MenuKeyboard>
              </MenuItem>

              <MenuItem
                id="generateReport"
                isDisabled={testOrders.length === 0}
              >
                <FileCheckIcon />
                {accession.status === "final"
                  ? "Corrected report"
                  : "Generate report"}
                <MenuKeyboard>P</MenuKeyboard>
              </MenuItem>
            </RoleCondition>
            <MenuItem id="upload">
              <UploadIcon />
              Upload file
            </MenuItem>
            {/* <MenuItem id="clone">
              <CopyPlusIcon />
              Clone accession
            </MenuItem> */}
            {/* <MenuItem id="instrument">
              <CpuIcon />
              Stage for instrument
            </MenuItem> */}
            <MenuSubTrigger>
              <MenuItem>
                <StatusIcon
                  status={accession.status}
                  noColor
                  data-slot="icon"
                />
                Set status
              </MenuItem>
              <Menu>
                {statusCommands.map(({ status, label, disableOn }) => (
                  <MenuItem
                    key={status}
                    onAction={() => {
                      commands.setStatus(status);
                    }}
                    isDisabled={
                      accession.status === status ||
                      disableOn?.includes(accession.status)
                    }
                  >
                    <StatusIcon status={status} />
                    {label}
                  </MenuItem>
                ))}
              </Menu>
            </MenuSubTrigger>
            <MenuSubTrigger>
              <MenuItem>
                <ChevronsUpIcon />
                Set priority
              </MenuItem>
              <Menu onAction={onAction}>
                <MenuItem id="setPriorityStat">
                  <ChevronsUpIcon />
                  STAT
                </MenuItem>
                <MenuItem id="setPriorityRoutine">
                  <CheckCircle2Icon />
                  Routine
                </MenuItem>
              </Menu>
            </MenuSubTrigger>
            <MenuSeparator />
          </>
        )}
        <MenuSubTrigger>
          <MenuItem isDisabled={referenceLabs.length === 0}>
            <FileIcon />
            Print requisition
          </MenuItem>
          <Menu>
            {referenceLabs.map((lab) => (
              <MenuItem
                key={lab.id}
                onAction={() => {
                  navigate({
                    to: "/accessions/$id/requisition",
                    params: { id: accession.id },
                    search: { labId: lab.id },
                  });
                }}
              >
                <FileIcon />
                {lab.name}
              </MenuItem>
            ))}
          </Menu>
        </MenuSubTrigger>

        <MenuItem id="printLabels">
          <TagIcon />
          Print labels
          <MenuKeyboard>LL</MenuKeyboard>
        </MenuItem>

        <MenuItem id="printArchiveLabels">
          <TagIcon />
          Print archive labels
          <MenuKeyboard>LA</MenuKeyboard>
        </MenuItem>

        <MenuSeparator />

        <MenuSubTrigger>
          <MenuItem>
            <PencilIcon />
            Edit
          </MenuItem>
          <Menu onAction={onAction}>
            <MenuItem id="addTests">
              <FlaskConicalIcon />
              Add tests
            </MenuItem>
            <MenuItem id="addSample">
              <TestTubeIcon />
              Add sample
            </MenuItem>
            <MenuSeparator />
            <MenuItem id="editDonor">
              <User2Icon />
              Edit donor info
            </MenuItem>
            <MenuItem id="moveToSubject">
              <CopyPlusIcon />
              Move accession to donor
            </MenuItem>
            <MenuItem id="editClient">
              <ContactIcon />
              Edit client info
            </MenuItem>
            <MenuItem id="editAdditional">
              <PackageIcon />
              Edit accession info
            </MenuItem>
            <RoleCondition roles={["blis_lab_manager"]}>
              <MenuItem id="editBilling">
                <DollarSignIcon />
                Edit billing
              </MenuItem>
            </RoleCondition>
          </Menu>
        </MenuSubTrigger>

        <RoleCondition
          roles={["blis_lab_manager", "blis_lab_tech", "blis_lab_assistant"]}
        >
          <MenuSeparator />
        </RoleCondition>
        <RoleCondition
          roles={["blis_lab_manager", "blis_lab_tech", "blis_lab_assistant"]}
        >
          {accession.status === "cancelled" ? (
            <MenuItem id="restore">
              <FileUpIcon />
              Restore accession
            </MenuItem>
          ) : (
            <MenuItem id="cancel">
              <XCircleIcon />
              Cancel accession
            </MenuItem>
          )}
        </RoleCondition>
        <RoleCondition roles={["blis_lab_manager"]}>
          <MenuItem id="delete" isDanger>
            <TrashIcon />
            Delete accession
          </MenuItem>
        </RoleCondition>
      </Menu>
    </MenuTrigger>
  );
}

function useReferenceLabs() {
  const { testOrders } = useAccession();
  const referenceOrgs = useMemo(
    () =>
      Array.from(
        new Set(
          testOrders
            .map((to) => to.testSpec.referenceOrg)
            .filter((org) => !!org),
        ),
      ),
    [testOrders],
  );

  return referenceOrgs;
}
